import { Row } from '@atbay/cyber-components'
import { EIssueSeverity } from '@core/modules/issues/constants'
import { getHighestSeverity } from '@routes/digital-footprint/routes/assets/components/assetsTable/utils'
import { TECHNOLOGY_TYPE_LABELS } from './constants'

export const sortByTypeLabel = (
    rowA: Row<TTechnology>,
    rowB: Row<TTechnology>
) =>
    TECHNOLOGY_TYPE_LABELS[rowA.original.type].localeCompare(
        TECHNOLOGY_TYPE_LABELS[rowB.original.type]
    )

export const sortByLastSeen = (
    rowA: Row<TTechnology>,
    rowB: Row<TTechnology>
) => {
    const aValue = rowA.original.last_seen
    const bValue = rowB.original.last_seen
    if (!aValue && !bValue) return 0
    if (!aValue) return 1
    if (!bValue) return -1
    return new Date(aValue).getTime() - new Date(bValue).getTime()
}

export const buildOpenIssuesByTechnologyName = (
    allIssues: TIssue[]
): Record<string, TIssue[]> =>
    allIssues.reduce<Record<string, TIssue[]>>((map, issue) => {
        if (issue.status !== 'open') return map
        return (issue.technologies ?? []).reduce((acc, technology) => {
            const name = technology?.name
            if (!name) return acc
            acc[name] = acc[name] ? [...acc[name], issue] : [issue]
            return acc
        }, map)
    }, {})

export const sortBySeverity = (
    rowA: Row<TTechnology>,
    rowB: Row<TTechnology>
) => {
    const aRank = EIssueSeverity[getHighestSeverity(rowA.original.issues)]
    const bRank = EIssueSeverity[getHighestSeverity(rowB.original.issues)]
    return aRank - bRank
}

export const sortByOpenIssuesCount =
    (openIssuesByName: Record<string, TIssue[]>) =>
    (rowA: Row<TTechnology>, rowB: Row<TTechnology>) => {
        const aCount = openIssuesByName[rowA.original.name]?.length ?? 0
        const bCount = openIssuesByName[rowB.original.name]?.length ?? 0
        return aCount - bCount
    }

export const sortTechnologiesByIssuesThenName = (
    technologies: TTechnology[],
    openIssuesByName: Record<string, TIssue[]>
): TTechnology[] =>
    [...technologies].sort((a, b) => {
        const aCount = openIssuesByName[a.name]?.length ?? 0
        const bCount = openIssuesByName[b.name]?.length ?? 0
        if (bCount !== aCount) return bCount - aCount
        return (a.name || '').localeCompare(b.name || '')
    })

export const sortAssociatedTechnologies = <
    T extends TTechnology & { isClickable: boolean },
>(
    technologies: T[]
): T[] => {
    const byName = (a: T, b: T) => (a.name ?? '').localeCompare(b.name ?? '')
    const clickable = technologies.filter((tech) => tech.isClickable)
    const nonClickable = technologies.filter((tech) => !tech.isClickable)
    return [...clickable.sort(byName), ...nonClickable.sort(byName)]
}
